<template>
    <div>
        <h5>All Incidents</h5>

        <div class="card">
            <div class="flex justify-content-between">
                <h6>Search Criteria:</h6>
                <div>
                    <Button label="Add New Incident" class="mb-2" @click="addIncident" />
                </div>
            </div>

            <Accordion :activeIndex="0">
                <AccordionTab header="Use as few or as many as you wish, partial matches are acceptable.">

                    <div class="p-fluid formgrid grid flex flex-wrap">
                        <div class="field col-12 sm:col-6 md:col-3">
                            <base-text-input
                                v-model="search.incident_number"
                                label="Incident No"
                                :autofocus="true" />
                        </div>
                        <div class="field col-12 sm:col-6 md:col-3">
                            <base-calendar-input v-model="search.incident_date_from" label="Incident Date From" dateFormat="dd-mm-yy" />
                        </div>
                        <div class="field col-12 sm:col-6 md:col-3">
                            <base-calendar-input v-model="search.incident_date_to" label="Incident Date To" dateFormat="dd-mm-yy" />
                        </div>
                        <div class="field col-12 sm:col-6 md:col-3">
                            <base-dropdown
                                v-model="search.category"
                                :options="incidentCategory"
                                optionLabel="description"
                                optionValue="code"
                                label="Category"
                                :filter="false"
                            />
                        </div>
                        <div class="col-12 flex justify-content-end">
                            <div class="flex">
                                <Button type="button" icon="pi pi-filter" label="Filter" class="p-button-outlined p-button-success ml-2" @click="applyFilter" />
                                <Button type="button" icon="pi pi-filter-slash" label="Clear" class="p-button-outlined p-button-danger ml-2" @click="clearFilter" />
                            </div>
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>
        </div>


        <DataTable

            :multiSortMeta="preSort"
            v-model:first="offset"
            :rows="numberOfRows"

            :value="allIncidents"
            :lazy="true"
            :paginator="true"

            ref="dt"
            dataKey="id"
            :totalRecords="totalRecords"
            :loading="loading"
            @page="onPage($event)"
            @sort="onSort($event)"
            removableSort
            sortMode="multiple"
            responsiveLayout="scroll"
            :rowsPerPageOptions="[5, 10, 20, 50, 100]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #empty> No incidents available. </template>
            <template #loading> Loading incidents. Please wait. </template>
            <Column field="id" header="Id" style="width: 4%"></Column>
            <Column field="incident_number" header="Incident No" style="width: 5%"></Column>
            <Column field="priority" header="Category" style="width: 2%">
                <template #body="slotProps">
                    <span v-if="parseInt(slotProps.data.category) === IncidentCategory.INCIDENT">
                        Incident
                        <i class="pi pi-bell" style="color: red"></i>
                    </span>
                    <span v-if="parseInt(slotProps.data.category) === IncidentCategory.EXERCISE">
                        Exercise
                        <i class="pi pi-megaphone" style="color: blue"></i>
                    </span>
                    <span v-if="parseInt(slotProps.data.category) === IncidentCategory.TRAINING">
                        Training
                        <i class="pi pi-flag" style="color: green"></i>
                    </span>
                    <span v-if="parseInt(slotProps.data.category) === IncidentCategory.NON_OPERATIONAL">
                        Non Operational
                        <i class="pi pi-cog" style="color: gray"></i>
                    </span>
                </template>
            </Column>
            <Column field="incident_date" header="Incident Date" style="width: 5%">
                <template #body="slotProps">
                    {{formatDate(slotProps.data?.incident_date)}}
                </template>
            </Column>
            <Column header="Type" style="width: 5%">
                <template #body="slotProps">
                    {{incidentTypes.get(slotProps.data?.incident_type_id)}}
                </template>
            </Column>
            <Column header="Summary" style="width: 20%">
                <template #body="slotProps">
                    {{slotProps.data?.summary}}
                </template>
            </Column>
            <Column header="Debriefs" style="width: 1%">
                <template #body="slotProps">
                    {{slotProps.data?.debriefs.length.toString()}}
                </template>
            </Column>
            <Column field="actions" header="ACTIONS" style="width: 5%">
                <template #body="slotProps">
                    <span class="p-column-title">ACTIONS</span>
                    <Button v-show="authStore.hasPermission(UserPermission.WRITE_INCIDENT)" icon="pi pi-pencil" class="p-button-rounded p-button-success mr-1" @click="editIncident(slotProps.data.id)" v-tooltip="'Edit Task'" />
                    <!--<Button v-show="authStore.hasPermission(UserPermission.READ_INCIDENT)" icon="pi pi-eye" class="p-button-rounded p-button-info mr-1" @click="editIncident(slotProps.data.id)" v-tooltip="'View Task'" />-->
                    <Button
                        v-show="authStore.hasPermission(UserPermission.DELETE_INCIDENT)"
                        icon="pi pi-trash"
                        class="p-button-rounded p-button-danger"
                        @click="confirmRequest({ id: slotProps.data.id }, deleteIncident)"
                        v-tooltip="'Delete Incident'"
                        :disabled="slotProps.data?.debriefs.length > 0"
                    />
                </template>
            </Column>
        </DataTable>



    </div>
</template>

<script setup>

import { useRouter } from 'vue-router';
import { computed, onMounted, reactive, ref } from 'vue';
import {
    buildPagination, getFilter,
    getOrder,
    getOrderForHttp,
    getPage,
    getPerPage, setFilter, setOrder,
    setPage, setPerPage,
} from '@/composables/PaginationComposable';
import { authApi } from '@/utilities/axios';
import { authStore } from '@/store/global-stores';
import hasOwn from 'core-js/internals/has-own-property';
import { DateTime } from 'luxon';
import { UserPermission } from '@/modules/permission/enum';
import { useConfirmRemove } from '@/composables/ConfirmComposable';
import { showToast } from '@/utilities/toast-handler';
import { IncidentCategory } from '../enum';
import { getCategoriesForIncidentList } from '@/product/ams/constants/index.js';

const { confirmRequest } = useConfirmRemove();
const router = useRouter();

const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

const search = ref({
    incident_number: null,
    incident_type_id: null,
    category: null,
    incident_date_from: null,
    incident_date_to: null,
})

const allIncidents = ref([])
const totalRecords = ref(0)
const loading = ref(false)

const incidentTypes = reactive( new Map() )

const incidentCategory = getCategoriesForIncidentList();

const pageList = 'incidentList'
const incidents_api_url = process.env.VUE_APP_API_VERSION + '/incident';
const incidents_type_api_url = process.env.VUE_APP_API_VERSION + '/incident-types';

onMounted(() => {
    buildPagination(pageList, 0, 10, [{field: "id", order: -1}])

    loadIncidentTypes()
    loadIncidents()
});

function deleteIncident(id) {
    loading.value = true

    const params = {
        company_id: company_id.value,
        }

    authApi.delete(`${incidents_api_url}/${id}`, {
        params: params,
    }).then((response) => {

        if (response.statusCode === 200) {
            showToast({ severity: 'info', summary: 'Incident deleted' }, 'Incident deleted');

            loadIncidents()
        } else {
            showToast({ severity: 'info', summary: 'Cannot delete' }, 'Cannot delete');
        }
    }).catch((error) => {
        console.error(error);
        showToast({ severity: 'info', summary: 'Cannot delete' }, 'Cannot delete');
    }).finally(()=>{
        loading.value = true
    })

}

function editIncident(id) {
    router.push({ name: 'IncidentCreateUpdate', query: { incidentId: id } })
}

function clearFilter() {
    search.value.incident_number = null
    search.value.incident_type_id = null
    search.value.category = null
    search.value.incident_date_from = null
    search.value.incident_date_to = null

    applyFilter()
}

function applyFilter() {
    setFilter(pageList, 'incident_number', search.value.incident_number)
    setFilter(pageList, 'incident_type_id', search.value.incident_type_id)
    setFilter(pageList, 'category', search.value.category)
    setFilter(pageList, 'incident_date_from', search.value.incident_date_from)
    setFilter(pageList, 'incident_date_to', search.value.incident_date_to)

    loadIncidents()
}

function flatIncidentTypes(parent, currentItem) {

    if (parent === null) {
        // top level
        incidentTypes.set(currentItem.id, currentItem.name)
    } else {
        const parentName = incidentTypes.get(parent)
        incidentTypes.set(currentItem.id, parentName +'/'+ currentItem.name)
    }

    if (hasOwn(currentItem, 'children')) {
        currentItem.children.forEach(child => {
            flatIncidentTypes(currentItem.id, child)
        })
    }
}

function loadIncidentTypes() {
    incidentTypes.value = []

    authApi.get(incidents_type_api_url, {
        params: {
            paginate: false
        },
    }).then((response) => {
        if (response.statusCode === 200) {
            if (hasOwn(response, 'data')) {
                response.data.forEach(incidentType => {
                    flatIncidentTypes(null, incidentType)
                })
            }
        }
    })
}

function addIncident() {
    router.push({ name: 'IncidentCreateUpdate' });
}

const offset = computed(() => {
    return getPage(pageList) * getPerPage(pageList)
})

const preSort = computed(() => {
    return getOrder(pageList)
})

const numberOfRows = computed(() => {
    return getPerPage(pageList)
})

const onPage = (event) => {
    setPage(pageList, event.page)
    setPerPage(pageList, event.rows)
    loadIncidents();
};

const onSort = (event) => {
    setOrder(pageList, event.multiSortMeta)
    loadIncidents();
};

function loadIncidents() {
    loading.value = true

    let page_size = getPerPage(pageList);
    let page = getPage(pageList);
    const sortOrder = getOrderForHttp(pageList)

    const incident_number = getFilter(pageList, 'incident_number')
    const incident_type_id = getFilter(pageList, 'incident_type_id')
    const category = getFilter(pageList, 'category')
    const incident_date_from = getFilter(pageList, 'incident_date_from')
    const incident_date_to = getFilter(pageList, 'incident_date_to')

    let params = {
        company_id: company_id.value,

        page_size: page_size,
        page: page + 1,
        sort: sortOrder,
    }

    if (incident_number !== null) {
        params.incident_number = incident_number
    }
    if (incident_type_id !== null) {
        params.incident_type_id = incident_type_id
    }
    if (category !== null) {
        params.category = category
    }
    if (incident_date_from !== null) {
        params.incident_date_from = incident_date_from
    }
    if (incident_date_to !== null) {
        params.incident_date_to = incident_date_to
    }

    // console.log('filters', params)
    // console.log('incident_date_from', incident_date_from)

    authApi.get(incidents_api_url, {
        params: params,
    }).then((response) => {

        if (response.statusCode === 200) {
            if (hasOwn(response, 'data')) {
                totalRecords.value = response.data.total;

                if (hasOwn(response.data, 'data')) {
                    allIncidents.value = response.data.data
                }
            }
        }
    }).finally(() => {
        loading.value = false
    });
}

function formatDate(dateString) {

    if (dateString && dateString.length > 0) {
        if (DateTime.fromISO(dateString).isValid) {
            return DateTime.fromISO(dateString).setLocale('en-GB').toLocaleString({
                weekday: 'short',
                month: 'short',
                day: '2-digit',
                year: 'numeric',
            })
        } else if (DateTime.fromSQL(dateString).isValid) {
            return DateTime.fromSQL(dateString).setLocale('en-GB').toLocaleString({
                weekday: 'short',
                month: 'short',
                day: '2-digit',
                year: 'numeric',
            })
        } else {
            console.log('formatDate',dateString, DateTime.fromSQL(dateString) )
        }
    } else {
        return ''
    }
}

</script>

<style scoped>

</style>